import { getGetRecipesQueryKey, useDeleteRecipesRecipeId, useGetCategories, useGetProductsList, useGetRecipes } from '@agos/api-client/menu'
import type { Recipe } from '@agos/api-client/model'
import { T, formatMoney, parseMoneyToCents, tt, useAuth } from '@agos/shared'
import {
  ConfirmDialog,
  DataTable,
  FilterBar,
  FilterSelect,
  PageHeader,
  StatusBadge,
  cn,
  toast,
  type BadgeColor,
  type Column, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { ChefHat, ChevronDown, Clock, Edit2, Package, Plus, ShoppingBag, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import { RecipeModal } from './RecipeModal'
import { recipeFilterOptions } from './recipe-categories'
import { recipeRightsFor, recipesReadOnlyNote } from './recipe-rights'

/**
 * Ported from legacy `pages/recipes.tsx`. Spec: `docs/pages/manager-menu/recipes.md`.
 *
 * A recipe maps a dish to the stock it consumes, so the kitchen knows what a
 * plate costs. **Cost and margin are main_admin only** — a cook may read a
 * recipe, only an administrator sees what it costs. That is a permission
 * boundary, ported exactly, not a layout preference.
 */

const PAGE_SIZE = 15

const CATEGORY_BADGE_COLORS: Record<string, BadgeColor> = {
  Pizza: 'orange',
  Cestoviny: 'amber',
  Bowle: 'green',
  Boxy: 'blue',
  Prílohy: 'violet',
  Dresingy: 'pink',
  Nápoje: 'cyan',
  Alkohol: 'red',
  Koktaily: 'emerald',
  Káva: 'zinc',
}

/** `1 porcia`, `2–4 porcie`, `5+ porcií`. */
function servingsLabel(servings: number): string {
  if (servings === 1) return tt('porcia')
  if (servings < 5) return tt('porcie')
  return tt('porcií')
}

export function RecipesPage() {
  const queryClient = useQueryClient()
  const { user, hasPermission } = useAuth()
  const isAdmin = user?.systemRole === 'main_admin'
  /* §9.758, BACKEND.md §10A.413 - the writes ask `recipes_edit` and `recipes_delete`. They asked nothing, so every
     visitor was offered a create, an edit and a delete the server refused. (`isAdmin` above is the cost and margin
     columns, a reading this entry does not touch.) */
  const rights = recipeRightsFor(hasPermission)
  const readOnlyNote = recipesReadOnlyNote(rights)

  const { data: recipes } = useGetRecipes()
  const { data: products } = useGetProductsList()
  const deleteRecipe = useDeleteRecipesRecipeId()

  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')
  const [expandedRecipeId, setExpandedRecipeId] = useState<number | null>(null)
  const [editing, setEditing] = useState<Recipe | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [pendingDeletion, setPendingDeletion] = useState<Recipe | null>(null)

  const productNames = useMemo(() => {
    const names = new Map<number, string>()
    for (const product of products ?? []) {
      if (product.id !== undefined) names.set(product.id, product.name ?? '')
    }
    return names
  }, [products])

  const allRecipes = useMemo(() => recipes ?? [], [recipes])
  // §9.349 - the franchise's own categories, which is what the filter should have been offering.
  const { data: categoryData } = useGetCategories()

  // §9.349, the owner's C8: *"When I use filter, I see only some of them"*. It offered the values in
  // use - seven, where the owner knows eighteen - so a category with no recipes yet could not be
  // chosen and five of the seven were spellings their category list never held. Now both: every
  // category, and every value in use that is not one, so the 57 recipes filed under those stay
  // findable and the reason they look odd is on the screen.
  const categoryOptions = useMemo(
    () => recipeFilterOptions(categoryData, allRecipes.map((recipe) => recipe.category)),
    [categoryData, allRecipes],
  )

  const visibleRecipes = useMemo(() => {
    let list = allRecipes
    if (category !== 'all') list = list.filter((recipe) => recipe.category === category)

    const query = search.trim().toLowerCase()
    if (query) {
      list = list.filter(
        (recipe) =>
          recipe.name?.toLowerCase().includes(query) ||
          recipe.category?.toLowerCase().includes(query),
      )
    }
    return list
  }, [allRecipes, category, search])

  const refreshRecipes = () => queryClient.invalidateQueries({ queryKey: getGetRecipesQueryKey() })

  const toggleExpanded = (recipeId: number | undefined) =>
    setExpandedRecipeId((current) => (current === recipeId ? null : (recipeId ?? null)))

  const allColumns: Column<Recipe>[] = [
    {
      key: 'name',
      header: tt('Názov'),
      sortable: true,
      sortValue: (recipe) => recipe.name ?? '',
      render: (recipe) => (
        <div>
          <span className="font-bold text-foreground">
            <T>{recipe.name}</T>
          </span>
          {recipe.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              <T>{recipe.description}</T>
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: tt('Kategória'),
      sortable: true,
      sortValue: (recipe) => recipe.category ?? '',
      render: (recipe) => (
        <StatusBadge
          label={recipe.category ?? ''}
          color={CATEGORY_BADGE_COLORS[recipe.category ?? ''] ?? 'zinc'}
        />
      ),
    },
    {
      key: 'product',
      header: tt('Produkt'),
      sortable: true,
      sortValue: (recipe) =>
        recipe.productId ? (productNames.get(recipe.productId) ?? '') : '',
      render: (recipe) => {
        if (!recipe.productId) return <span className="text-sm text-muted-foreground">-</span>
        return (
          <span className="flex items-center gap-1.5 text-sm text-foreground">
            <ShoppingBag className="w-3.5 h-3.5 text-primary shrink-0" />
            <span className="truncate max-w-[140px]">
              {productNames.get(recipe.productId) ?? `#${recipe.productId}`}
            </span>
          </span>
        )
      },
    },
    {
      key: 'servings',
      header: tt('Porcie'),
      sortable: true,
      sortValue: (recipe) => recipe.servings ?? 0,
      render: (recipe) => <span className="text-sm">{recipe.servings}</span>,
    },
    {
      key: 'ingredients',
      header: tt('Suroviny'),
      render: (recipe) => (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            toggleExpanded(recipe.id)
          }}
          className={cn(
            'flex items-center gap-1.5 text-sm font-medium transition-colors',
            expandedRecipeId === recipe.id
              ? 'text-primary'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Package className="w-3.5 h-3.5" />
          {recipe.ingredients?.length ?? 0}
          <ChevronDown
            className={cn(
              'w-3 h-3 transition-transform',
              expandedRecipeId === recipe.id && 'rotate-180',
            )}
          />
        </button>
      ),
    },
    {
      key: 'cost',
      header: tt('Náklad'),
      sortable: true,
      sortValue: (recipe) => recipe.totalCost ?? 0,
      render: (recipe) => (
        <span
          className={cn(
            'text-sm font-semibold',
            (recipe.totalCost ?? 0) > 0 ? 'text-red-400' : 'text-muted-foreground',
          )}
        >
          {(recipe.totalCost ?? 0) > 0 ? formatMoney(recipe.totalCost) : '–'}
        </span>
      ),
    },
    {
      key: 'sellingPrice',
      header: tt('Pred. cena'),
      sortable: true,
      sortValue: (recipe) => recipe.sellingPrice ?? 0,
      render: (recipe) => (
        <span
          className={cn(
            'text-sm font-semibold',
            (recipe.sellingPrice ?? 0) > 0 ? 'text-emerald-500' : 'text-muted-foreground',
          )}
        >
          {(recipe.sellingPrice ?? 0) > 0 ? formatMoney(recipe.sellingPrice) : '–'}
        </span>
      ),
    },
    {
      key: 'margin',
      header: tt('Marža'),
      render: (recipe) => {
        const sellingPriceCents = parseMoneyToCents(recipe.sellingPrice)
        if (sellingPriceCents <= 0) return <span className="text-sm text-muted-foreground">–</span>

        const marginCents = sellingPriceCents - parseMoneyToCents(recipe.totalCost)
        const percentage = Math.round((marginCents / sellingPriceCents) * 100)
        return (
          <span
            className={cn(
              'text-sm font-semibold',
              marginCents > 0 ? 'text-emerald-500' : 'text-red-400',
            )}
          >
            {formatMoney(marginCents / 100)} ({percentage}%)
          </span>
        )
      },
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (recipe) => (
        <div className="flex gap-1 justify-end">
          {rights.edit && (
            <button
              type="button"
              aria-label={tt('Upraviť')}
              data-recipe-edit={recipe.id}
              onClick={(event) => {
                event.stopPropagation()
                setEditing(recipe)
              }}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          )}
          {rights.delete && (
            <button
              type="button"
              aria-label={tt('Zmazať')}
              data-recipe-delete={recipe.id}
              onClick={(event) => {
                event.stopPropagation()
                setPendingDeletion(recipe)
              }}
              className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-500"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ]

  // A cook may read a recipe; only a main administrator sees what it costs. And a row with no action to offer
  // draws no empty actions column (§9.758).
  const columns = allColumns
    .filter((column) => isAdmin || !['cost', 'margin'].includes(column.key))
    .filter((column) => column.key !== 'actions' || rights.edit || rights.delete)

  const expandedRecipe = visibleRecipes.find((recipe) => recipe.id === expandedRecipeId)
  const categoryCount = new Set(allRecipes.map((recipe) => recipe.category)).size

  return (
    <div className="space-y-6">
      <PageHeader
        title={tt('Recepty')}
        description={`${allRecipes.length} ${tt('receptov v')} ${categoryCount} ${tt('kategóriách')}`}
        icon={<ChefHat className="w-5 h-5" />}
        actions={
          rights.edit ? (
            <button
              type="button"
              data-recipe-create
              onClick={() => setIsCreating(true)}
              className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              <Plus className="w-4 h-4" /> <T>Pridať recept</T>
            </button>
          ) : undefined
        }
      />

      {readOnlyNote !== null && (
        <p className="text-[11px] text-amber-500" data-recipes-read-only>
          {readOnlyNote}
        </p>
      )}

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať recept...') }}
        filters={
          <FilterSelect value={category} onChange={setCategory} options={categoryOptions} />
        }
      />

      <DataTable
        columns={columns}
        data={visibleRecipes}
        rowKey={(recipe) => recipe.id ?? 0}
        pageSize={PAGE_SIZE}
        emptyMessage={tt('Žiadne recepty')}
        onRowClick={(recipe) => toggleExpanded(recipe.id)}
      />

      {expandedRecipe && (
        <div className="bg-card border border-border rounded-xl p-5 -mt-2 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ChefHat className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold text-foreground">
                <T>{expandedRecipe.name}</T>
              </h3>
              <StatusBadge
                label={expandedRecipe.category ?? ''}
                color={CATEGORY_BADGE_COLORS[expandedRecipe.category ?? ''] ?? 'zinc'}
              />
            </div>
            <button
              type="button"
              onClick={() => setExpandedRecipeId(null)}
              className="text-muted-foreground hover:text-foreground transition-colors text-xs"
            >
              <T>Zavrieť</T>
            </button>
          </div>

          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            {expandedRecipe.prepTime ? (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> <T>Príprava:</T> {expandedRecipe.prepTime} min
              </span>
            ) : null}
            <span className="flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> {expandedRecipe.servings}{' '}
              {servingsLabel(expandedRecipe.servings ?? 0)}
            </span>
            {expandedRecipe.productId ? (
              <span className="flex items-center gap-1">
                <ShoppingBag className="w-3.5 h-3.5 text-primary" /> <T>Produkt:</T>{' '}
                {productNames.get(expandedRecipe.productId) ?? `#${expandedRecipe.productId}`}
              </span>
            ) : null}
          </div>

          {expandedRecipe.description && (
            <p className="text-sm text-muted-foreground italic">
              <T>{expandedRecipe.description}</T>
            </p>
          )}

          {(expandedRecipe.ingredients?.length ?? 0) > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                <T>Ingrediencie</T>
              </h4>
              <div className="space-y-1.5">
                {expandedRecipe.ingredients?.map((ingredient) => (
                  <div
                    key={ingredient.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-muted/30 text-sm"
                  >
                    <span className="text-foreground font-medium">
                      <T>{ingredient.name}</T>
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground font-mono text-xs">
                        {ingredient.quantity} {ingredient.unit}
                      </span>
                      {isAdmin && (ingredient.cost ?? 0) > 0 && (
                        // FB-016: legacy writes "EUR" here and "€" in the table
                        // two centimetres above.
                        <span className="text-red-400 font-mono text-xs">
                          {formatMoney(ingredient.cost)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {isAdmin && (expandedRecipe.totalCost ?? 0) > 0 && (
                <div className="flex justify-end mt-2 pt-2 border-t border-border/50">
                  <span className="text-sm font-bold text-foreground">
                    <T>Celkový náklad:</T> {formatMoney(expandedRecipe.totalCost)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isCreating && (
        <RecipeModal
          mayEdit={rights.edit}
          onClose={() => setIsCreating(false)}
          onSaved={() => {
            setIsCreating(false)
            void refreshRecipes()
          }}
        />
      )}

      {editing && (
        <RecipeModal
          recipe={editing}
          mayEdit={rights.edit}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void refreshRecipes()
          }}
        />
      )}

      {/* FB-017: legacy asks with the browser's native confirm(). */}
      <ConfirmDialog
        open={pendingDeletion !== null}
        onClose={() => setPendingDeletion(null)}
        onConfirm={() => {
          const recipeId = pendingDeletion?.id
          if (recipeId === undefined) return
          deleteRecipe.mutate(
            { recipeId },
            {
              onSuccess: () => {
                void refreshRecipes()
                setPendingDeletion(null)
                toast({ title: tt('Recept zmazaný') })
              },
            },
          )
        }}
        loading={deleteRecipe.isPending}
        variant="danger"
        title={tt('Zmazať recept?')}
        description={`${tt('Recept')} "${pendingDeletion?.name ?? ''}" ${tt('sa odstráni aj so svojimi ingredienciami.')}`}
        confirmLabel={tt('Zmazať')}
      />
    </div>
  )
}
